// Boite de reception (02/10, demande de Louka) : dans la colonne de la page Assistant, l'entree
// « Boîte de réception » ouvre une petite fenetre (BoiteDeReception.tsx) au lieu d'une page entiere de
// l'app. Le modele de navigation de l'app annonce la boite (`boite: { api, nonLus }`) ; le chat lit et
// marque les notifications directement dans l'API de l'app (meme origine, cookie de session, pas de
// CSRF sur /api/**), puis previent la page du haut, qui relit sa boite (meme evenement que la page
// d'ouverture de l'app, route-de-notification.ts) et renvoie un modele a jour.
// Module pur : `fetch` et la fenetre sont passes en parametre.
import type { FenetreMinimale } from "@app/lib/compta/mode_integre";
import { estIntegre } from "@app/lib/compta/mode_integre";

/** Evenement que la page Assistant de l'app ecoute pour relire sa boite (meme nom cote app). */
export const EVENEMENT_BOITE = "pirouette:boite";

const PREFIXE_DUST = "dust:";
const CONVERSATION = /^[A-Za-z0-9_-]+$/;
const API = /^\/api\/[A-Za-z0-9_\-/]*$/;

export type BoiteDuModele = { api: string; nonLus: number };

export type NotificationBoite = {
  id: string;
  titre: string;
  extrait: string;
  creeLe: string;
  lu: boolean;
  /** La conversation du chat a ouvrir (notification « dust:<sId> »), sinon null. */
  conversation: string | null;
};

export type ListeBoite = { nonLus: number; elements: NotificationBoite[] };

type Recuperer = (url: string, init?: RequestInit) => Promise<Response>;

/** La boite annoncee par le modele de l'app, ou null (ancienne app, champ invalide). */
export function boiteDuModele(nav: { boite?: unknown }): BoiteDuModele | null {
  const b = nav.boite as { api?: unknown; nonLus?: unknown } | undefined;
  if (typeof b !== "object" || b === null) {
    return null;
  }
  if (typeof b.api !== "string" || !API.test(b.api) || b.api.includes("..")) {
    return null;
  }
  const n = typeof b.nonLus === "number" && b.nonLus > 0 ? b.nonLus : 0;
  return { api: b.api.replace(/\/+$/, ""), nonLus: Math.floor(n) };
}

/** La conversation du chat d'une notification « dust:<sId> », sinon null. */
export function conversationDe(sessionId: unknown): string | null {
  if (typeof sessionId !== "string" || !sessionId.startsWith(PREFIXE_DUST)) {
    return null;
  }
  const id = sessionId.slice(PREFIXE_DUST.length).trim();
  return CONVERSATION.test(id) ? id : null;
}

export function extrait(texte: unknown, max = 160): string {
  if (typeof texte !== "string") {
    return "";
  }
  const t = texte.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/** « il y a 5 min », « hier », « 12 août » : le moment d'une notification, en francais. */
export function tempsRelatif(
  iso: string,
  maintenant: Date = new Date()
): string {
  const date = new Date(iso);
  const ms = maintenant.getTime() - date.getTime();
  if (Number.isNaN(ms)) {
    return "";
  }
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) {
    return "à l'instant";
  }
  if (minutes < 60) {
    return `il y a ${minutes} min`;
  }
  const heures = Math.floor(minutes / 60);
  if (heures < 24) {
    return `il y a ${heures} h`;
  }
  const jours = Math.floor(heures / 24);
  if (jours < 2) {
    return "hier";
  }
  if (jours < 7) {
    return `il y a ${jours} j`;
  }
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    timeZone: "Europe/Paris",
  });
}

function options(init?: RequestInit): RequestInit {
  return { credentials: "include", ...init };
}

export async function lireNotifications(
  recuperer: Recuperer,
  api: string
): Promise<ListeBoite | null> {
  try {
    const r = await recuperer(api, options());
    if (!r.ok) {
      return null;
    }
    const corps = (await r.json()) as {
      unread?: unknown;
      items?: unknown;
    };
    const items = Array.isArray(corps.items) ? corps.items : [];
    const elements: NotificationBoite[] = [];
    for (const brut of items) {
      const n = brut as Record<string, unknown>;
      if (typeof n?.id !== "string") {
        continue;
      }
      elements.push({
        id: n.id,
        titre:
          typeof n.title === "string" && n.title ? n.title : "Notification",
        extrait: extrait(n.body),
        creeLe: typeof n.createdAt === "string" ? n.createdAt : "",
        lu: n.read === true,
        conversation: conversationDe(n.sessionId),
      });
    }
    const nonLus =
      typeof corps.unread === "number"
        ? corps.unread
        : elements.filter((e) => !e.lu).length;
    return { nonLus, elements };
  } catch {
    return null;
  }
}

export async function marquerLue(
  recuperer: Recuperer,
  api: string,
  id: string
): Promise<boolean> {
  try {
    const r = await recuperer(
      `${api}/${encodeURIComponent(id)}/read`,
      options({ method: "POST" })
    );
    return r.ok;
  } catch {
    return false;
  }
}

export async function marquerToutLu(
  recuperer: Recuperer,
  api: string
): Promise<boolean> {
  try {
    const r = await recuperer(`${api}/read-all`, options({ method: "POST" }));
    return r.ok;
  } catch {
    return false;
  }
}

interface FenetreBoite extends FenetreMinimale {
  parent?: unknown;
}

/** Previent la page Assistant de l'app : elle relit sa boite (compteurs) sans attendre son sondage. */
export function prevenirLApp(fenetre: FenetreBoite | undefined): void {
  try {
    if (!fenetre || !estIntegre(fenetre)) {
      return;
    }
    const parent = fenetre.parent as
      | { dispatchEvent?: (e: Event) => boolean }
      | undefined;
    parent?.dispatchEvent?.(new Event(EVENEMENT_BOITE));
  } catch {
    // Parent d'une autre origine : le sondage de l'app suivra.
  }
}
